import { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { portalApi } from '@/api/portal';
import { usePlatformBrand } from '@/contexts/PlatformBrandContext';

type TabKey = 'overview' | 'frontend' | 'notify';

const DOMAIN_PLACEHOLDER = '{{API_DOMAIN}}';

const createRequest = `{
  "app_id": "APP202607160001",
  "out_trade_no": "ORDER202607160001",
  "pay_type": "alipay_h5",
  "subject": "会员充值",
  "amount": 3000,
  "currency": "CNY",
  "notify_url": "${DOMAIN_PLACEHOLDER}/demo/merchant/notify",
  "return_url": "${DOMAIN_PLACEHOLDER}/demo/merchant/result",
  "timestamp": 1784160000,
  "nonce": "RANDOM123",
  "sign": "hmac_sha256_hex"
}`;

const usdtRequest = `{
  "app_id": "APP202607160001",
  "out_trade_no": "USDT202607160001",
  "pay_type": "usdt_trc20",
  "subject": "USDT充值",
  "amount": 3001,
  "currency": "USDT",
  "notify_url": "${DOMAIN_PLACEHOLDER}/demo/merchant/notify",
  "return_url": "${DOMAIN_PLACEHOLDER}/demo/merchant/result",
  "timestamp": 1784160000,
  "nonce": "USDT123456",
  "sign": "hmac_sha256_hex"
}`;

const createResponse = `{
  "code": 0,
  "message": "success",
  "data": {
    "trade_no": "P202607160001ABCD",
    "out_trade_no": "ORDER202607160001",
    "pay_type": "alipay_h5",
    "amount": 3000,
    "currency": "CNY",
    "pay_url": "https://upstream.example.com/pay/123",
    "qr_code": "",
    "page_url": "${DOMAIN_PLACEHOLDER}/api/v1/gateway/pay/page?trade_no=P202607160001ABCD",
    "pay_address": "",
    "pay_chain": "",
    "expire_time": "2026-07-16 18:30:00",
    "status": "pending"
  }
}`;

const usdtResponse = `{
  "code": 0,
  "message": "success",
  "data": {
    "trade_no": "P202607160009USDT",
    "out_trade_no": "USDT202607160001",
    "pay_type": "usdt_trc20",
    "amount": 3001,
    "currency": "USDT",
    "pay_url": "${DOMAIN_PLACEHOLDER}/api/v1/gateway/pay/page?trade_no=P202607160009USDT",
    "qr_code": "",
    "page_url": "${DOMAIN_PLACEHOLDER}/api/v1/gateway/pay/page?trade_no=P202607160009USDT",
    "pay_address": "TXXXXXXXXXXXXXXXXXXXXXXXXXXX",
    "pay_chain": "TRC20",
    "expire_time": "2026-07-16 18:30:00",
    "status": "pending"
  }
}`;

const queryResponse = `{
  "code": 0,
  "message": "success",
  "data": {
    "trade_no": "P202607160009USDT",
    "out_trade_no": "USDT202607160001",
    "channel_trade_no": "CH202607160001",
    "pay_type": "usdt_trc20",
    "amount": 3001,
    "currency": "USDT",
    "pay_url": "${DOMAIN_PLACEHOLDER}/api/v1/gateway/pay/page?trade_no=P202607160009USDT",
    "qr_code": "",
    "page_url": "${DOMAIN_PLACEHOLDER}/api/v1/gateway/pay/page?trade_no=P202607160009USDT",
    "pay_address": "TXXXXXXXXXXXXXXXXXXXXXXXXXXX",
    "pay_chain": "TRC20",
    "paid_amount": 3001,
    "chain_txid": "abcd1234txid",
    "confirmations": 18,
    "status": "paid",
    "paid_at": "2026-07-16 18:22:33"
  }
}`;

const notifyExample = `{
  "app_id": "APP202607160001",
  "merchant_no": "M202607160001",
  "trade_no": "P202607160009USDT",
  "out_trade_no": "USDT202607160001",
  "channel_trade_no": "CH202607160001",
  "pay_type": "usdt_trc20",
  "amount": 3001,
  "currency": "USDT",
  "pay_address": "TXXXXXXXXXXXXXXXXXXXXXXXXXXX",
  "pay_chain": "TRC20",
  "paid_amount": 3001,
  "chain_txid": "abcd1234txid",
  "confirmations": 18,
  "status": "paid",
  "paid_at": "2026-07-16 18:22:33",
  "timestamp": 1784160123,
  "nonce": "NOTIFY001",
  "sign": "hmac_sha256_hex"
}`;

const payTypes = [
  ['alipay_qr', '支付宝扫码', 'CNY', '可返回 qr_code / page_url'],
  ['alipay_h5', '支付宝跳转', 'CNY', '可返回 pay_url / page_url'],
  ['wechat_qr', '微信扫码', 'CNY', '可返回 qr_code / page_url'],
  ['wechat_h5', '微信跳转', 'CNY', '可返回 pay_url / page_url'],
  ['usdt_trc20', 'USDT-TRC20', 'USDT', '建议直接打开 page_url，或单独展示地址与二维码'],
  ['bank', '银行卡', 'CNY', '按通道配置返回'],
];

function injectDomain(text: string, domain: string) {
  return text.split(DOMAIN_PLACEHOLDER).join(domain);
}

function SectionTitle({ title, desc }: { title: string; desc?: string }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-slate-100">{title}</h3>
      {desc ? <p className="mt-1 text-sm text-slate-400">{desc}</p> : null}
    </div>
  );
}

function CodeBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="relative">
      <button
        className="absolute right-3 top-3 rounded border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:text-white"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? '已复制' : '复制'}
      </button>
      <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg border border-slate-700/60 bg-slate-900/80 p-4 pr-16 text-xs font-mono text-slate-300">
        {text}
      </pre>
    </div>
  );
}

export default function ApiDocsPage() {
  const { platformName } = usePlatformBrand();
  const [tab, setTab] = useState<TabKey>('overview');
  const [apiDomain, setApiDomain] = useState('');

  useEffect(() => {
    portalApi
      .profile()
      .then((profile) => {
        setApiDomain(profile.apiDomain || window.location.origin);
      })
      .catch(() => setApiDomain(window.location.origin));
  }, []);

  const baseUrl = useMemo(() => (apiDomain || window.location.origin).replace(/\/+$/, ''), [apiDomain]);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="inline-flex rounded-lg p-1 tech-card">
          {[
            ['overview', '接口总览'],
            ['frontend', '前端接入'],
            ['notify', '异步通知'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key as TabKey)}
              className={`rounded-md px-4 py-2 text-sm font-medium ${
                tab === key ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="rounded-lg border border-slate-700/60 bg-slate-900/60 p-4 text-sm text-slate-300 tech-card">
          接口基地址：<code className="break-all font-mono text-blue-300">{baseUrl}</code>
        </div>

        {tab === 'overview' ? (
          <div className="space-y-5">
            <div className="tech-card space-y-5 p-6">
              <SectionTitle title="接入结论" desc={`${platformName} 建议统一后端接口接入，USDT 在前端单独处理展示。`} />
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-4 text-sm text-emerald-200">
                后端接口：融合
                <br />
                前端展示：单独处理 USDT
              </div>
              <div className="text-sm leading-7 text-slate-300">
                所有支付方式都使用同一套下单、查单、通知接口。区别主要在前端处理：
                法币支付通常跳转或展示二维码；USDT 支付需要展示钱包地址、链类型、金额和链上等待状态，所以建议单独做一个前端展示分支。
              </div>
            </div>

            <div className="tech-card space-y-5 p-6">
              <SectionTitle title="统一下单接口" desc="接口路径：POST /api/v1/gateway/pay/create" />
              <CodeBlock text={injectDomain(createRequest, baseUrl)} />
              <SectionTitle title="USDT 下单示例" desc="USDT 使用同一接口，但 amount 单位是 6 位最小单位。" />
              <CodeBlock text={injectDomain(usdtRequest, baseUrl)} />
            </div>

            <div className="tech-card space-y-5 p-6">
              <SectionTitle title="支付方式与返回处理" desc="前端根据 pay_type 和返回字段决定跳转还是展示。" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700 text-slate-500">
                      <th className="py-2 pr-4 text-left">pay_type</th>
                      <th className="py-2 pr-4 text-left">含义</th>
                      <th className="py-2 pr-4 text-left">币种</th>
                      <th className="py-2 text-left">前端处理</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {payTypes.map(([value, name, currency, result]) => (
                      <tr key={value}>
                        <td className="py-2 pr-4 font-mono text-blue-300">{value}</td>
                        <td className="py-2 pr-4 text-slate-300">{name}</td>
                        <td className="py-2 pr-4 text-slate-400">{currency}</td>
                        <td className="py-2 text-slate-400">{result}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="tech-card space-y-5 p-6">
              <SectionTitle title="法币返回示例" />
              <CodeBlock text={injectDomain(createResponse, baseUrl)} />
              <SectionTitle title="USDT 返回示例" />
              <CodeBlock text={injectDomain(usdtResponse, baseUrl)} />
              <SectionTitle title="查单返回示例" />
              <CodeBlock text={injectDomain(queryResponse, baseUrl)} />
            </div>
          </div>
        ) : null}

        {tab === 'frontend' ? (
          <div className="space-y-5">
            <div className="tech-card space-y-4 p-6">
              <SectionTitle title="前端接入建议" desc="最终推荐：法币和 USDT 共用下单接口，但前端按两套体验处理。" />
              <div className="text-sm leading-7 text-slate-300">
                推荐优先级：
                <br />
                法币支付：优先打开 <code className="font-mono text-blue-300">page_url</code>，其次打开 <code className="font-mono text-blue-300">pay_url</code>，最后自己渲染 <code className="font-mono text-blue-300">qr_code</code>。
                <br />
                USDT 支付：优先打开 <code className="font-mono text-blue-300">page_url</code>；如果商户自建页面，则单独展示地址、链类型、金额和二维码。
              </div>
            </div>

            <div className="tech-card space-y-4 p-6">
              <SectionTitle title="法币前端流程" />
              <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-300">
                <li>调用 `/api/v1/gateway/pay/create` 创建订单。</li>
                <li>收到返回后，优先跳转 `page_url`。</li>
                <li>如果没有 `page_url`，则跳转 `pay_url`。</li>
                <li>如果只有 `qr_code`，则前端自己生成二维码。</li>
                <li>支付结果不要只看页面跳转，要以后端通知或查单为准。</li>
              </ol>
            </div>

            <div className="tech-card space-y-4 p-6">
              <SectionTitle title="USDT 前端流程" />
              <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-300">
                <li>调用同一个 `/api/v1/gateway/pay/create`。</li>
                <li>如果平台返回 `page_url`，建议直接打开平台托管收银台。</li>
                <li>如果商户自建页面，展示 `pay_address`、`pay_chain`、`amount`、`trade_no`。</li>
                <li>二维码内容建议使用 `pay_address` 生成。</li>
                <li>页面只负责展示和轮询，不负责最终入账判断。</li>
                <li>最终结果以异步通知或查单为准。</li>
              </ol>
            </div>

            <div className="tech-card space-y-4 p-6">
              <SectionTitle title="为什么 USDT 建议单独提出来" />
              <ul className="list-disc space-y-2 pl-5 text-sm text-slate-300">
                <li>USDT 不是普通跳转支付，要显示链上地址。</li>
                <li>USDT 金额与法币统一按两位小数传输，`1 USDT = 100`。</li>
                <li>USDT 需要等待链上到账和确认。</li>
                <li>USDT 更适合独立收银台页面，不适合完全复用微信/支付宝模板。</li>
              </ul>
            </div>

            <div className="tech-card space-y-4 p-6">
              <SectionTitle title="推荐对外口径" />
              <CodeBlock
                text={`${platformName} 使用统一支付网关接口接入；法币与 USDT 共用同一套下单、查单、通知接口。USDT 因为需要展示链上收款地址、金额和二维码，所以前端页面建议单独处理。`}
              />
            </div>
          </div>
        ) : null}

        {tab === 'notify' ? (
          <div className="space-y-5">
            <div className="tech-card space-y-4 p-6">
              <SectionTitle title="异步通知说明" desc="支付成功后，平台向 notify_url 发送 POST JSON。" />
              <CodeBlock text={injectDomain(notifyExample, baseUrl)} />
              <div className="rounded-lg border border-slate-700/60 bg-slate-900/60 p-4 text-sm text-slate-300">
                商户处理成功后，必须返回纯文本 <code className="font-mono text-emerald-300">SUCCESS</code>。
                <br />
                页面跳转只用于展示，不能当成最终支付成功依据。
              </div>
            </div>

            <div className="tech-card space-y-4 p-6">
              <SectionTitle title="关键提醒" />
              <ul className="list-disc space-y-2 pl-5 text-sm text-slate-300">
                <li>一定要做验签。</li>
                <li>一定要做重复通知幂等处理。</li>
                <li>一定要保存 `trade_no` 和 `out_trade_no` 的映射。</li>
                <li>USDT 订单建议同时保存 `pay_address`、`pay_chain`、`chain_txid`。</li>
              </ul>
            </div>
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
